import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  useTheme,
} from '@mui/material';
import { Metric, TrendType } from '../types';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';

interface MetricTableProps {
  metrics: Metric[];
}

const MetricTable: React.FC<MetricTableProps> = ({ metrics }) => {
  const theme = useTheme();

  // Get metric table colors from theme
  const metricTableColors = theme.palette.metricTable!;
  
  // Extract colors for easier access
  const baselineHeaderColor = metricTableColors?.baseline?.header;
  const baselineCellColor = metricTableColors?.baseline?.cell;
  const bestInClassHeaderColor = metricTableColors?.bestInClass?.header;
  const bestInClassCellColor = metricTableColors?.bestInClass?.cell;
  const lastMonthHeaderColor = metricTableColors?.lastMonth?.header;
  const lastMonthCellColor = metricTableColors?.lastMonth?.cell;

  const getTrendIcon = (trend: TrendType) => {
    if (trend === 'up') {
      return <ArrowUpward sx={{ fontSize: 20, color: theme.palette.success.main }} />;
    }
    return <ArrowDownward sx={{ fontSize: 20, color: theme.palette.error.main }} />;
  };

  const formatValue = (value: number | null, symbol: string = ''): string => {
    if (value === null) return 'NA';
    return `${value}${symbol}`;
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: 1.5,
        boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
        overflow: 'auto',
      }}
    >
      <Table sx={{ minWidth: 500 }}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: '1.1rem',
                padding: '12px 16px',
                backgroundColor: 'transparent',
              }}
            >
              
            </TableCell>
            <TableCell
              align="left"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: '1.1rem',
                padding: '12px 16px',
                backgroundColor: baselineHeaderColor,
              }}
            >
              Baseline
            </TableCell>
            <TableCell
              align="left"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: '1.1rem',
                padding: '12px 16px',
                backgroundColor: bestInClassHeaderColor,
              }}
            >
              Best in Class
            </TableCell>
            <TableCell
              align="left"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: '1.1rem',
                padding: '12px 16px',
                backgroundColor: lastMonthHeaderColor,
              }}
            >
              Last Month
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {metrics.map((metric, index) => (
            <TableRow
              key={index}
              sx={{
                '& td': {
                  padding: '12px 16px',
                  fontSize: '1.1rem',
                },
              }}
            >
              <TableCell
                sx={{
                  backgroundColor: 'transparent',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                    {metric.metric}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="left"
                sx={{
                  backgroundColor: baselineCellColor,
                }}
              >
                <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                  {formatValue(metric.baseline, metric.symbol)}
                </Typography>
              </TableCell>
              <TableCell
                align="left"
                sx={{
                  backgroundColor: bestInClassCellColor,
                }}
              >
                <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                  {formatValue(metric.bestInClass, metric.symbol)}
                </Typography>
              </TableCell>
              <TableCell
                align="left"
                sx={{
                  backgroundColor: lastMonthCellColor,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 0.5,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                    {formatValue(metric.lastMonth, metric.symbol)}
                  </Typography>
                  {getTrendIcon(metric.trend)}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MetricTable;

